<template>
  <div v-if="variant === 'new'" class="survey-page survey-page--new">
    <NewToolShell :title="$t('newSurvey.shellTitle')" :subtitle="$t('newSurvey.shellSubtitle')">
      <router-link to="/new" class="survey-back">{{ $t('newSurvey.backHome') }}</router-link>

      <h2 v-if="userLoggedIn" class="survey-page__h2">{{ $t('newSurvey.selectTeamForMaturity') }}</h2>
      <h2 v-else class="survey-page__h2">{{ $t('newSurvey.guestSurveyTitle') }}</h2>

      <!-- Выбор команды (вход в аккаунт) -->
      <section v-if="!selectedTeam && userLoggedIn" class="sv-card">
        <div class="team-selection">
          <div class="team-buttons">
            <button
              v-for="team in teams"
              :key="team.id"
              type="button"
              class="team-btn"
              :class="{ active: selectedTeam === team.id }"
              @click="selectTeam(team.id, team.name)"
            >
              {{ team.name }}
            </button>
          </div>
          <div class="create-btn" role="button" tabindex="0" @click="showTeamModal = true" @keydown.enter="showTeamModal = true">
            + {{ $t("survey.createNewTeam") }}
          </div>
        </div>
      </section>

      <!-- Старт без аккаунта -->
      <section v-else-if="!selectedTeam && !userLoggedIn" class="sv-card sv-card--guest-start">
        <p class="guest-disclaimer">{{ $t('newSurvey.guestDisclaimer') }}</p>
        <p class="guest-login-hint">
          <router-link to="/login">{{ $t('newSurvey.guestHaveAccount') }}</router-link>
          <span aria-hidden="true"> · </span>
          <router-link to="/register">{{ $t('newSurvey.guestRegister') }}</router-link>
        </p>
        <div class="team-profile-field">
          <label class="team-profile-field__label" for="guest-team-label">{{ $t('newSurvey.guestTeamLabel') }}</label>
          <input
            id="guest-team-label"
            v-model="guestTeamLabel"
            type="text"
            class="guest-team-input"
            :placeholder="$t('newSurvey.guestTeamPlaceholder')"
            maxlength="120"
            autocomplete="organization"
          />
        </div>
        <div class="team-profile-field">
          <label class="team-profile-field__label" for="guest-maturity-team-profile">{{ $t('newSurvey.teamTypeLabel') }}</label>
          <select
            id="guest-maturity-team-profile"
            v-model="teamProfileType"
            class="team-profile-select"
          >
            <option v-for="opt in maturityTeamOptions" :key="`g-${opt.value}`" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="team-profile-field survey-mode-field">
          <span class="team-profile-field__label">{{ $t('newSurvey.surveyModeLabel') }}</span>
          <div class="survey-mode-options" role="radiogroup" :aria-label="$t('newSurvey.surveyModeLabel')">
            <label class="survey-mode-option">
              <input v-model="surveyMode" type="radio" value="simple" @change="onSurveyModeChange" />
              {{ $t('newSurvey.surveyModeSimple') }}
            </label>
            <label class="survey-mode-option">
              <input v-model="surveyMode" type="radio" value="full" @change="onSurveyModeChange" />
              {{ $t('newSurvey.surveyModeFull') }}
            </label>
          </div>
          <p class="survey-mode-hint">
            {{ surveyMode === 'simple' ? $t('newSurvey.surveyModeSimpleHint') : $t('newSurvey.surveyModeFullHint') }}
          </p>
        </div>
        <button type="button" class="modern-button purple guest-start-btn" @click="startGuestSurvey">
          {{ $t('newSurvey.startGuest') }}
        </button>
      </section>

      <template v-else>
        <section class="sv-card sv-card--meta">
          <div class="team-name">{{ $t('newSurvey.teamPrefix') }}: {{ selectedTeamName }}</div>
          <div class="team-profile-field">
            <label class="team-profile-field__label" for="maturity-team-profile">{{ $t('newSurvey.teamTypeLabel') }}</label>
            <select
              id="maturity-team-profile"
              v-model="teamProfileType"
              class="team-profile-select"
              @change="onTeamProfileChange"
            >
              <option v-for="opt in maturityTeamOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="team-profile-field survey-mode-field">
            <span class="team-profile-field__label">{{ $t('newSurvey.surveyModeLabel') }}</span>
            <div class="survey-mode-options" role="radiogroup" :aria-label="$t('newSurvey.surveyModeLabel')">
              <label class="survey-mode-option">
                <input v-model="surveyMode" type="radio" value="simple" @change="onSurveyModeChange" />
                {{ $t('newSurvey.surveyModeSimple') }}
              </label>
              <label class="survey-mode-option">
                <input v-model="surveyMode" type="radio" value="full" @change="onSurveyModeChange" />
                {{ $t('newSurvey.surveyModeFull') }}
              </label>
            </div>
            <p class="survey-mode-hint">
              {{ surveyMode === 'simple' ? $t('newSurvey.surveyModeSimpleHint') : $t('newSurvey.surveyModeFullHint') }}
            </p>
          </div>
        </section>

        <section class="sv-card sv-card--muted">
          <p class="disclaimer">{{ activeDisclaimer }}</p>
        </section>

        <section class="sv-card sv-card--tracker">
          <div class="tracker-head">
            <p class="tracker-label">{{ $t('newSurvey.progressHint') }}</p>
            <p v-if="questions.length" class="tracker-step">{{ questionStepLabel }}</p>
          </div>
          <div class="survey-progress-bar" role="progressbar" :aria-valuenow="progressFillPercent" aria-valuemin="0" aria-valuemax="100">
            <div class="survey-progress-bar__fill" :style="{ width: progressFillPercent + '%' }" />
          </div>
          <div class="tracker-scroll">
            <div class="question-tracker" role="tablist" :aria-label="$t('survey.question')">
              <button
                type="button"
                v-for="(q, index) in questions"
                :key="q.id"
                class="tracker-dot"
                :aria-label="`${$t('survey.question')} ${index + 1}`"
                :aria-selected="currentQuestionIndex === index"
                :class="{
                  answered: answers[q.id],
                  active: currentQuestionIndex === index,
                }"
                @click="currentQuestionIndex = index"
              />
            </div>
          </div>
        </section>

        <section v-if="currentQuestion" class="sv-card question-card">
          <button
            v-if="currentQuestionIndex > 0"
            type="button"
            class="back-btn"
            :aria-label="$t('maturity.prev')"
            @click="prevQuestion"
          >
            ⬅
          </button>
          <h2 class="question-text">{{ currentQuestion.question }}</h2>

          <div v-if="variant === 'new' && currentQuestion" class="question-ai-help">
            <button
              type="button"
              class="question-ai-help__toggle"
              :disabled="clarifyLoading"
              :aria-expanded="clarifyOpen ? 'true' : 'false'"
              @click="onClarifyToggle"
            >
              {{ clarifyOpen ? $t('newSurvey.aiHelpHide') : $t('newSurvey.aiHelp') }}
            </button>
            <div
              v-show="clarifyOpen"
              class="question-ai-help__panel"
              role="region"
              :aria-label="$t('newSurvey.aiHelp')"
            >
              <p v-if="clarifyLoading" class="question-ai-help__status">{{ $t('newSurvey.aiHelpLoading') }}</p>
              <p v-else-if="clarifyError" class="question-ai-help__err">{{ clarifyError }}</p>
              <div v-else-if="clarifyText" class="question-ai-help__text">{{ clarifyText }}</div>
            </div>
          </div>

          <div
            class="answer-options"
            :class="{ 'answer-options--binary': variant === 'new' && surveyMode === 'simple' }"
          >
            <button
              v-for="item in levelEntries(currentQuestion)"
              :key="item.key"
              type="button"
              class="level-option"
              :class="[
                variant === 'new' && surveyMode === 'simple' ? `level-option--binary-${item.key}` : `level-option--n${item.index}`,
                { selected: answers[currentQuestion.id] === item.key },
              ]"
              @click="answerQuestion(item.key)"
            >
              <span v-if="!(variant === 'new' && surveyMode === 'simple')" class="level-option__badge">{{ item.index }}</span>
              <span class="level-option__text">{{ item.desc }}</span>
            </button>
          </div>
        </section>

        <div v-if="allAnswered && currentQuestionIndex === questions.length - 1" class="survey-submit-wrap">
          <button type="button" class="modern-button purple" @click="submitAssessment">
            {{ submitButtonLabel }}
          </button>
        </div>
      </template>
    </NewToolShell>
  </div>

  <div v-else class="survey-container">
    <h1>{{ $t('newSurvey.selectTeamForMaturity') }}</h1>

    <div v-if="!selectedTeam" class="team-selection">
      <div class="team-buttons">
        <button
          v-for="team in teams"
          :key="team.id"
          class="team-btn"
          :class="{ active: selectedTeam === team.id }"
          @click="selectTeam(team.id, team.name)"
        >
          {{ team.name }}
        </button>
      </div>
      <div class="create-btn" role="button" tabindex="0" @click="showTeamModal = true" @keydown.enter="showTeamModal = true">
        + {{ $t("survey.createNewTeam") }}
      </div>
    </div>

    <div v-else>
      <h2 class="team-name">{{ $t('newSurvey.teamPrefix') }}: {{ selectedTeamName }}</h2>
      <p class="disclaimer">{{ $t('newSurvey.disclaimer') }}</p>

      <div class="tracker-strip" aria-hidden="false">
        <p class="tracker-label">{{ $t('newSurvey.progressHint') }}</p>
        <div class="question-tracker" role="tablist" :aria-label="$t('survey.question')">
          <button
            type="button"
            v-for="(q, index) in questions"
            :key="q.id"
            class="tracker-dot"
            :aria-label="`${$t('survey.question')} ${index + 1}`"
            :aria-selected="currentQuestionIndex === index"
            :class="{
              answered: answers[q.id],
              active: currentQuestionIndex === index,
            }"
            @click="currentQuestionIndex = index"
          />
        </div>
      </div>

      <div v-if="currentQuestion" class="question-card">
        <button v-if="currentQuestionIndex > 0" type="button" class="back-btn" :aria-label="$t('maturity.prev')" @click="prevQuestion">⬅</button>
        <h2 class="question-text">{{ currentQuestion.question }}</h2>

        <div class="answer-options">
          <button
            v-for="item in levelEntries(currentQuestion)"
            :key="item.key"
            type="button"
            class="level-option"
            :class="[
              `level-option--n${item.index}`,
              { selected: answers[currentQuestion.id] === item.key },
            ]"
            @click="answerQuestion(item.key)"
          >
            <span class="level-option__badge">{{ item.index }}</span>
            <span class="level-option__text">{{ item.desc }}</span>
          </button>
        </div>
      </div>

      <div style="text-align: center; margin-top: 30px">
        <button
          v-if="allAnswered && currentQuestionIndex === questions.length - 1"
          class="modern-button purple"
          type="button"
          @click="submitAssessment"
        >
          {{ $t('newSurvey.submitResults') }}
        </button>
      </div>
    </div>
  </div>

  <CreateTeamModal v-model="showTeamModal" v-model:team-name="newTeamName" @confirm="createTeam" />
</template>


<script>
import axios from "axios";
import NewToolShell from "@/views/NewToolShell.vue";
import CreateTeamModal from "@/components/CreateTeamModal.vue";

/** Кэш ответа POST /temp_results, если cookie-сессия на CDN/прокси не приходит */
const GUEST_SURVEY_RESULTS_STORAGE_KEY = "growboard_guest_survey_results_v1";

export default {
  components: { NewToolShell, CreateTeamModal },
  props: {
    variant: { type: String, default: "legacy" },
  },
  data() {
    return {
      teams: [],
      selectedTeam: null,
      selectedTeamName: "",
      newTeamName: "",
      questions: [],
      answers: {},
      showTeamModal: false,
      currentQuestionIndex: 0,
      avgTimePerQuestion: 1.5,
      teamProfileType: "it",
      surveyMode: "simple",
      clarifyOpen: false,
      clarifyLoading: false,
      clarifyError: "",
      clarifyText: "",
      userLoggedIn: false,
      guestTeamLabel: "",
    };
  },

  watch: {
    currentQuestionIndex() {
      if (this.variant !== "new") return;
      // Не тянем пояснение автоматически на новый вопрос — только после явного «Помощь нейросети»
      this.clarifyOpen = false;
      this.clarifyLoading = false;
      this.clarifyText = "";
      this.clarifyError = "";
    },
    teamProfileType() {
      if (this.variant !== "new") return;
      this.clearClarify();
    },
  },

  computed: {
    activeDisclaimer() {
      if (this.variant !== "new") return this.$t("newSurvey.disclaimer");
      const base =
        this.surveyMode === "simple"
          ? this.$t("newSurvey.disclaimerSimple")
          : this.$t("newSurvey.disclaimer");
      if (this.selectedTeam === "guest") {
        return `${base} ${this.$t("newSurvey.guestNotSavedHint")}`;
      }
      return base;
    },
    progress() {
      return this.questions.length ? (this.currentQuestionIndex / this.questions.length) * 100 : 0;
    },
    progressFillPercent() {
      if (!this.questions.length) return 0;
      return Math.min(100, ((this.currentQuestionIndex + 1) / this.questions.length) * 100);
    },
    questionStepLabel() {
      if (!this.questions.length) return "";
      return this.$t("newSurvey.questionProgress", {
        current: this.currentQuestionIndex + 1,
        total: this.questions.length,
      });
    },
    maturityTeamOptions() {
      const keys = [
        "it",
        "sales",
        "marketing",
        "construction_design",
        "product_research",
        "tech_writers",
        "project_team",
      ];
      return keys.map((value) => ({
        value,
        label: this.$t(`newSurvey.teamTypes.${value}`),
      }));
    },
    remainingTime() {
      return Math.max(0, Math.round((this.questions.length - this.currentQuestionIndex) * this.avgTimePerQuestion));
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || null;
    },
    allAnswered() {
      return Object.keys(this.answers).length === this.questions.length;
    },
    submitButtonLabel() {
      if (this.variant === "new" && this.selectedTeam === "guest") {
        return this.$t("newSurvey.guestSubmitResults");
      }
      return this.$t("newSurvey.submitResults");
    },
  },

  methods: {
    levelEntries(question) {
      if (this.variant === "new" && this.surveyMode === "simple") {
        return [
          { key: "optimal", index: 1, desc: this.$t("newSurvey.answerYes") },
          { key: "basic", index: 2, desc: this.$t("newSurvey.answerNo") },
        ];
      }
      const order = ["basic", "transitional", "growing", "normalization", "optimal"];
      if (!question?.levels) return [];
      return order
        .filter((k) => Object.prototype.hasOwnProperty.call(question.levels, k))
        .map((key, idx) => ({
          key,
          index: idx + 1,
          desc: question.levels[key],
        }));
    },
    clearClarify() {
      this.clarifyOpen = false;
      this.clarifyLoading = false;
      this.clarifyError = "";
      this.clarifyText = "";
    },
    onClarifyToggle() {
      if (this.variant !== "new" || !this.currentQuestion) return;
      this.clarifyOpen = !this.clarifyOpen;
      if (this.clarifyOpen) this.fetchQuestionClarify();
    },
    async fetchQuestionClarify() {
      if (!this.currentQuestion) return;
      this.clarifyLoading = true;
      this.clarifyError = "";
      this.clarifyText = "";
      try {
        const token = localStorage.getItem("token");
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await axios.post(
          "/question_clarify",
          {
            lang: this.$i18n.locale,
            team_type: this.teamProfileType,
            question_text: this.currentQuestion.question,
            category: this.currentQuestion.category,
            subcategory: this.currentQuestion.subcategory,
          },
          { headers }
        );
        this.clarifyText = (res.data && res.data.content) || "";
        if (!this.clarifyText) this.clarifyError = this.$t("newSurvey.aiHelpError");
      } catch (e) {
        const msg = e.response?.data?.error;
        this.clarifyError = typeof msg === "string" ? msg : this.$t("newSurvey.aiHelpError");
      } finally {
        this.clarifyLoading = false;
      }
    },
    onSurveyModeChange() {
      if (this.variant !== "new") return;
      this.clearClarify();
      this.answers = {};
      this.currentQuestionIndex = 0;
      if (this.selectedTeam) this.fetchQuestions();
    },
    async fetchTeams() {
      try {
        console.log("📡 Загружаем список команд...");
        const token = localStorage.getItem("token");

        if (!token) {
          if (this.variant === "new") {
            this.teams = [];
            return;
          }
          console.error("❌ Ошибка: Нет токена авторизации!");
          alert("🚫 Вы не авторизованы!");
          return;
        }

        const res = await axios.get("/user_teams", {
          headers: { Authorization: `Bearer ${token}` },
        });

        this.teams = res.data;
        console.log("✅ Команды загружены:", this.teams);
      } catch (error) {
        console.error("❌ Ошибка загрузки команд:", error.response?.data || error);
        alert("❌ Ошибка загрузки команд.");
      }
    },

    async fetchQuestions() {
      try {
        const lang = this.$i18n.locale;
        const params = { lang };
        if (this.variant === "new") {
          params.team_type = this.teamProfileType;
          params.survey_mode = this.surveyMode;
        }
        const res = await axios.get("/questions", { params });
        this.questions = res.data;
      } catch (error) {
        console.error("❌ Ошибка загрузки вопросов:", error);
        if (this.$toast?.error) this.$toast.error(this.$t("survey.errorLoadingQuestions"));
      }
    },

    onTeamProfileChange() {
      if (this.variant !== "new") return;
      this.clearClarify();
      this.answers = {};
      this.currentQuestionIndex = 0;
      if (this.selectedTeam) this.fetchQuestions();
    },

    startGuestSurvey() {
      if (this.variant !== "new") return;
      try {
        sessionStorage.removeItem(GUEST_SURVEY_RESULTS_STORAGE_KEY);
      } catch (_e) {
        /* ignore */
      }
      const name = (this.guestTeamLabel || "").trim() || this.$t("newSurvey.defaultGuestTeamName");
      this.selectTeam("guest", name);
    },

    selectTeam(teamId, teamName) {
      this.selectedTeam = teamId;
      this.selectedTeamName = teamName;
      if (this.variant === "new") {
        this.teamProfileType = this.teamProfileType || "it";
      }
      this.fetchQuestions();
    },

    async createTeam() {
      if (!this.newTeamName.trim()) {
        alert(this.$t("newSurvey.enterTeamName"));
        return;
      }

      try {
        console.log("📤 Создание команды...");
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("❌ Ошибка: Нет токена авторизации!");
          alert(this.$t("newSurvey.notAuthorized"));
          return;
        }

        const res = await axios.post(
          "/create_team",
          { team_name: this.newTeamName },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log("✅ Команда успешно создана:", res.data);

        await this.fetchTeams();
        const createdName = this.newTeamName.trim();
        const created = this.teams.find((t) => t.name === createdName);
        if (created) {
          this.selectTeam(created.id, created.name);
        }
        this.showTeamModal = false;
      } catch (error) {
        console.error("❌ Ошибка создания команды:", error.response?.data || error);
        alert(this.$t("newSurvey.teamCreateError"));
      }
    },

    answerQuestion(level) {
      this.answers[this.currentQuestion.id] = level;
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
      }
    },

    prevQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
      }
    },

    async submitAssessment() {
      try {
        console.log("📤 Отправка результатов...");

        if (this.variant === "new" && this.selectedTeam === "guest") {
          const res = await axios.post("/temp_results", {
            answers: this.answers,
            team_name: this.selectedTeamName,
            team_type: this.teamProfileType,
            survey_mode: this.surveyMode,
            lang: this.$i18n.locale,
          });
          try {
            if (res.data && res.data.results && typeof res.data.results === "object") {
              sessionStorage.setItem(GUEST_SURVEY_RESULTS_STORAGE_KEY, JSON.stringify(res.data));
            }
          } catch (_e) {
            /* quota / private mode */
          }
          this.$router.push({
            name: "AssessmentResults",
            params: {
              team_id: "0",
              assessment_id: "0",
            },
            query: { team_type: this.teamProfileType },
          });
          return;
        }

        const token = localStorage.getItem("token");

        if (!token) {
          console.error("❌ Ошибка: Нет токена авторизации!");
          alert(this.$t("newSurvey.notAuthorized"));
          return;
        }

        const payload = {
          team_id: this.selectedTeam,
          answers: this.answers,
        };
        if (this.variant === "new") {
          payload.team_type = this.teamProfileType;
        }

        const res = await axios.post("/submit_assessment", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const assessmentId = res.data.assessment_id; // ✅ берём ID созданной оценки

        alert(this.$t("newSurvey.resultsSaved"));

        // 🔁 Переход с передачей assessment_id в параметры
        this.$router.push({
          name: "AssessmentResults",
          params: {
            team_id: this.selectedTeam,
            assessment_id: assessmentId,
          },
          query: { team_type: this.teamProfileType },
        });
      } catch (error) {
        console.error("❌ Ошибка отправки:", error.response?.data || error);
        alert(this.$t("newSurvey.submitError"));
      }
    },

  },

  async mounted() {
    this.userLoggedIn = !!localStorage.getItem("token");
    if (this.variant === "new" && !this.userLoggedIn) {
      this.teams = [];
    } else {
      await this.fetchTeams();
    }
    const tid = this.$route.query.team_id;
    if (tid != null && tid !== "" && this.userLoggedIn) {
      const team = this.teams.find((t) => String(t.id) === String(tid));
      if (team) {
        this.selectTeam(team.id, team.name);
      }
    }
  },
};
</script>
<style scoped>
/* 🔷 Общий контейнер */
.survey-container {
  max-width: 900px;
  margin: auto;
  padding: 30px;
  text-align: center;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  animation: fadeInUp 0.6s ease;
}

/* 🔷 Заголовок */
h1 {
  font-size: 28px;
  font-weight: 800;
  color: #2c3e50;
  margin-bottom: 20px;
}

/* 🔹 Прогресс бар */
.progress-bar {
  position: relative;
  width: 100%;
  height: 10px;
  background: #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
  margin: 20px 0;
}
.progress {
  height: 100%;
  background: linear-gradient(90deg, #00c6ff, #0072ff);
  transition: width 0.4s ease-in-out;
}
.progress-text {
  position: absolute;
  top: -22px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
  font-weight: 600;
  color: #555;
}

/* 🔹 Вопрос */
.question-text {
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 20px;
  line-height: 1.4;
}

/* 🔹 Ответы */
.answer-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
}
.answer-options button {
  padding: 14px 20px;
  border-radius: 30px;
  background: #f0f0f0;
  color: #333;
  font-size: 16px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
.answer-options button:hover {
  background: #4caf50;
  color: #000000;
  transform: scale(1.03);
}
.answer-options button.selected {
  background: #4caf50;
  color: white;
  font-weight: bold;
}

/* 🔹 Кнопка отправки */
.send-button {
  margin-top: 25px;
  background: #8e44ad;
  color: white;
  padding: 14px 28px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.send-button:hover {
  background: #732d91;
  transform: scale(1.03);
}

/* 🔹 Анимация появления */
.fade-in {
  opacity: 0;
  transform: translateY(12px);
  animation: fadeInUp 0.6s ease forwards;
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 🔹 Плавная смена вопросов */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* 🔹 Выбор команды */
.team-selection {
  margin-bottom: 20px;
}
.team-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 15px;
  margin-top: 10px;
}
.team-btn {
  background: #3498db;
  border: none;
  padding: 12px 22px;
  font-size: 16px;
  font-weight: bold;
  color: white;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.team-btn:hover {
  background: #2980b9;
  transform: scale(1.05);
}
.team-btn.active {
  background: #2ecc71;
}

/* 🔹 Кнопка "Создать команду" */
.create-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #8e44ad;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  margin-top: 10px;
  transition: color 0.2s ease;
}
.create-btn:hover {
  color: #732d91;
}

.disclaimer {
  background: #f0f8ff;
  padding: 12px 16px;
  border-left: 5px solid #3498db;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 15px;
  color: #2c3e50;
  text-align: left;
}

.survey-container:not(.survey--new) .answer-options button {
  transition: 0.3s;
}
.survey-container:not(.survey--new) .answer-options button:nth-child(1) { background-color: rgba(231, 104, 124, 0.6); }
.survey-container:not(.survey--new) .answer-options button:nth-child(2) { background-color: rgba(231, 211, 104, 0.6); }
.survey-container:not(.survey--new) .answer-options button:nth-child(3) { background-color: rgba(104, 124, 231, 0.6); }
.survey-container:not(.survey--new) .answer-options button:nth-child(4) { background-color: rgba(104, 188, 231, 0.6); }
.survey-container:not(.survey--new) .answer-options button:nth-child(5) { background-color: rgba(124, 231, 104, 0.6); }

.tracker-strip {
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(10, 20, 45, 0.04);
  border: 1px solid rgba(10, 20, 45, 0.07);
}

.tracker-label {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(10, 20, 45, 0.55);
  text-align: left;
}

.question-tracker {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 14px;
  margin: 0;
  max-height: none;
}

.tracker-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  border: none;
  padding: 0;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tracker-dot::before {
  content: "";
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ccc;
  transition: inherit;
  flex-shrink: 0;
}
.tracker-dot.answered::before {
  background: #2ecc71;
}
.tracker-dot.active::before {
  border: 2px solid #3498db;
  background: white;
}

.modern-button {
  font-weight: 600;
  padding: 12px 20px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 220px;
}

/* 🟣 Фиолетовый стиль */
.modern-button.purple {
  background: linear-gradient(135deg, #8e44ad, #9b59b6);
}

.modern-button.purple:hover {
  background: linear-gradient(135deg, #732d91, #8e44ad);
  transform: scale(1.04);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

/* —— New UI (/new/survey) —— */
.survey-container.survey--new {
  max-width: 880px;
  margin: 0 auto 40px;
  padding: 28px 22px 36px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 20px;
  border: 1px solid rgba(10, 20, 45, 0.08);
  box-shadow: 0 22px 70px rgba(10, 20, 45, 0.1);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Roboto", sans-serif;
}

.survey--new h1 {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1a1a1a;
}

.survey--new .team-name {
  font-size: 18px;
  font-weight: 600;
  color: rgba(10, 20, 45, 0.75);
}

.survey--new .disclaimer {
  background: rgba(32, 90, 255, 0.06);
  border-left: 4px solid rgba(32, 90, 255, 0.55);
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.55;
}

.survey--new .question-card {
  position: relative;
  padding: 22px 18px 26px;
  border-radius: 18px;
  border: 1px solid rgba(10, 20, 45, 0.08);
  background: #fff;
  box-shadow: 0 4px 24px rgba(10, 20, 45, 0.06);
  animation: cardRise 380ms ease-out;
}

.survey--new .question-text {
  font-size: 20px;
  font-weight: 600;
  color: rgba(10, 20, 45, 0.9);
  padding-right: 44px;
}

.survey--new .tracker-strip {
  background: rgba(248, 250, 255, 0.95);
  border: 1px solid rgba(10, 20, 45, 0.08);
  box-shadow: none;
}

.survey--new .tracker-label {
  color: rgba(10, 20, 45, 0.5);
  font-weight: 600;
}

.survey--new .question-tracker {
  gap: 16px;
}

.survey--new .tracker-dot {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: transparent;
  padding: 0;
  margin: 0;
}
.survey--new .tracker-dot::before {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  background: rgba(10, 20, 45, 0.12);
}

.survey--new .tracker-dot.answered::before {
  background: rgba(34, 197, 94, 0.85);
}

.survey--new .tracker-dot.active::before {
  background: #fff;
  border: 2px solid rgba(32, 90, 255, 0.75);
  box-shadow: 0 0 0 1px rgba(32, 90, 255, 0.2);
  transform: scale(1.15);
}

.survey--new .answer-options {
  gap: 10px;
}

.survey--new .level-option {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 14px;
  border: 1px solid rgba(10, 20, 45, 0.1);
  font-weight: 500;
  text-align: left;
  line-height: 1.45;
  padding: 12px 14px;
  background: #fafbfc;
  color: rgba(16, 28, 58, 0.92);
  box-shadow: 0 1px 2px rgba(10, 20, 45, 0.04);
  animation: optionIn 280ms ease-out;
  cursor: pointer;
}

.survey--new .level-option__badge {
  flex: 0 0 auto;
  min-width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.95rem;
  background: rgba(10, 20, 45, 0.07);
  color: rgba(10, 20, 45, 0.88);
}

.survey--new .level-option__text {
  flex: 1;
  min-width: 0;
  font-size: 15px;
}

.survey--new .level-option:hover:not(.selected) {
  transform: translateY(-1px);
  border-color: rgba(32, 90, 255, 0.35);
  box-shadow: 0 6px 16px rgba(32, 90, 255, 0.08);
  background: rgba(32, 90, 255, 0.04);
}

.survey--new .level-option--n1 .level-option__badge { background: rgba(239, 68, 68, 0.12); color: #b91c1c; }
.survey--new .level-option--n2 .level-option__badge { background: rgba(245, 158, 11, 0.15); color: #b45309; }
.survey--new .level-option--n3 .level-option__badge { background: rgba(59, 130, 246, 0.12); color: #1d4ed8; }
.survey--new .level-option--n4 .level-option__badge { background: rgba(14, 165, 233, 0.12); color: #0369a1; }
.survey--new .level-option--n5 .level-option__badge { background: rgba(34, 197, 94, 0.14); color: #15803d; }

.survey--new .level-option.selected {
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.95), rgba(37, 99, 235, 0.88)) !important;
  color: #fff !important;
  border-color: rgba(32, 90, 255, 0.5) !important;
  font-weight: 600;
  box-shadow: 0 8px 22px rgba(32, 90, 255, 0.22);
}

.survey--new .level-option.selected .level-option__badge {
  background: rgba(255, 255, 255, 0.22) !important;
  color: #fff !important;
}

.survey--new .level-option.selected::after {
  display: none;
}

.survey--new .team-btn {
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.88), rgba(0, 194, 255, 0.72));
  border: 1px solid rgba(32, 90, 255, 0.25);
  box-shadow: 0 8px 22px rgba(32, 90, 255, 0.18);
}

.survey--new .team-btn:hover {
  filter: brightness(1.06);
  transform: translateY(-2px);
}

.survey--new .team-btn.active {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.92), rgba(16, 185, 129, 0.78));
  border-color: rgba(34, 197, 94, 0.35);
}

.survey--new .modern-button.purple {
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.92), rgba(0, 194, 255, 0.82));
  box-shadow: 0 12px 32px rgba(32, 90, 255, 0.22);
}

.survey--new .modern-button.purple:hover {
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.98), rgba(0, 194, 255, 0.9));
  transform: translateY(-2px);
  box-shadow: 0 16px 40px rgba(32, 90, 255, 0.28);
}

.survey--new {
  position: relative;
  overflow: hidden;
}

.survey--new::before {
  content: "";
  position: absolute;
  inset: -35% -25% auto auto;
  width: 360px;
  height: 360px;
  border-radius: 999px;
  background: radial-gradient(circle, rgba(120, 119, 255, 0.07), rgba(0, 194, 255, 0));
  pointer-events: none;
}

.survey--new .team-btn,
.survey--new .modern-button.purple,
.survey--new .back-btn,
.survey--new .level-option {
  position: relative;
  overflow: hidden;
}

.survey--new .team-btn::after,
.survey--new .modern-button.purple::after,
.survey--new .back-btn::after,
.survey--new .level-option::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, transparent 20%, rgba(255, 255, 255, 0.35), transparent 80%);
  transform: translateX(-130%);
  transition: transform 0.6s ease;
}

.survey--new .team-btn:hover::after,
.survey--new .modern-button.purple:hover::after,
.survey--new .back-btn:hover::after,
.survey--new .level-option:hover::after {
  transform: translateX(130%);
}

.survey--new .back-btn {
  border-radius: 12px;
  border: 1px solid rgba(10, 20, 45, 0.14);
  background: rgba(255, 255, 255, 0.92);
  padding: 7px 10px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(10, 20, 45, 0.08);
}

.survey--new .back-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(32, 90, 255, 0.35);
}

.survey--new .team-btn:focus-visible,
.survey--new .level-option:focus-visible,
.survey--new .answer-options button:focus-visible,
.survey--new .modern-button:focus-visible,
.survey--new .back-btn:focus-visible,
.survey--new .tracker-dot:focus-visible {
  outline: 3px solid rgba(32, 90, 255, 0.55);
  outline-offset: 2px;
}

@keyframes premiumFloat {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(-10px, 12px, 0) scale(1.04);
  }
}

@keyframes cardRise {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.99);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes optionIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* —— /new/survey shell (GrowBoard + NewToolShell) —— */
.survey-page--new {
  min-height: 100vh;
  background: radial-gradient(1100px 560px at 15% 10%, rgba(32, 90, 255, 0.12), transparent 60%),
    radial-gradient(900px 520px at 90% 18%, rgba(0, 194, 255, 0.1), transparent 62%),
    radial-gradient(800px 520px at 55% 95%, rgba(32, 90, 255, 0.08), transparent 58%), #f7f9ff;
  color: rgba(10, 20, 45, 0.92);
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
}

.survey-page--new .survey-back {
  display: inline-block;
  margin-bottom: 14px;
  font-weight: 600;
  font-size: 14px;
  color: rgba(32, 90, 255, 0.95);
  text-decoration: none;
}

.survey-page--new .survey-back:hover {
  text-decoration: underline;
}

.survey-page__h2 {
  margin: 0 0 18px;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(10, 20, 45, 0.92);
  text-align: left;
}

.survey-page--new .sv-card {
  border-radius: 18px;
  border: 1px solid rgba(10, 20, 45, 0.08);
  background: linear-gradient(175deg, rgba(255, 255, 255, 0.96), rgba(246, 249, 255, 0.9));
  box-shadow: 0 18px 56px rgba(10, 20, 45, 0.09);
  padding: 20px 18px 22px;
  margin-bottom: 16px;
  text-align: left;
}

.survey-page--new .sv-card--guest-start .guest-disclaimer {
  margin: 0 0 10px;
  line-height: 1.5;
  color: rgba(10, 20, 45, 0.78);
  font-size: 0.95rem;
}

.survey-page--new .sv-card--guest-start .guest-login-hint {
  margin: 0 0 18px;
  font-size: 0.9rem;
}

.survey-page--new .sv-card--guest-start .guest-login-hint a {
  color: #0072ff;
  text-decoration: none;
  font-weight: 600;
}

.survey-page--new .sv-card--guest-start .guest-login-hint a:hover {
  text-decoration: underline;
}

.survey-page--new .guest-team-input {
  width: 100%;
  max-width: 420px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(10, 20, 45, 0.14);
  font-size: 1rem;
  box-sizing: border-box;
}

.survey-page--new .guest-start-btn {
  margin-top: 18px;
}

.survey-page--new .sv-card--muted {
  background: rgba(255, 255, 255, 0.88);
}

.survey-page--new .sv-card--meta .team-name {
  font-size: 17px;
  font-weight: 700;
  color: rgba(10, 20, 45, 0.88);
  margin-bottom: 14px;
}

.team-profile-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
}

.team-profile-field__label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(10, 20, 45, 0.55);
}

.team-profile-select {
  width: 100%;
  max-width: 420px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(10, 20, 45, 0.14);
  background: rgba(248, 250, 255, 0.98);
  font-size: 15px;
  font-weight: 600;
  color: rgba(10, 20, 45, 0.9);
}

.team-profile-select:focus {
  outline: none;
  border-color: rgba(32, 90, 255, 0.45);
  box-shadow: 0 0 0 4px rgba(32, 90, 255, 0.12);
}

.survey-mode-field {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(10, 20, 45, 0.08);
}

.survey-mode-options {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 22px;
}

.survey-mode-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(10, 20, 45, 0.85);
  cursor: pointer;
}

.survey-mode-option input {
  accent-color: rgba(32, 90, 255, 0.9);
}

.survey-mode-hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(10, 20, 45, 0.55);
}

.survey-page--new .disclaimer {
  margin: 0;
  background: rgba(32, 90, 255, 0.06);
  border-left: 4px solid rgba(32, 90, 255, 0.55);
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(10, 20, 45, 0.82);
}

.tracker-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.survey-page--new .tracker-label {
  margin: 0;
}

.tracker-step {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: rgba(32, 90, 255, 0.92);
}

.survey-progress-bar {
  height: 8px;
  border-radius: 999px;
  background: rgba(10, 20, 45, 0.08);
  overflow: hidden;
  margin-bottom: 12px;
}

.survey-progress-bar__fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(32, 90, 255, 0.85), rgba(0, 194, 255, 0.75));
  transition: width 0.35s ease;
}

.tracker-scroll {
  overflow-x: visible;
  overflow-y: visible;
  padding-bottom: 4px;
}

.survey-page--new .tracker-scroll .question-tracker {
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px;
  row-gap: 10px;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.survey-page--new .tracker-dot {
  width: 30px;
  height: 30px;
  border-radius: 8px;
}

.survey-page--new .tracker-dot::before {
  width: 11px;
  height: 11px;
  border-radius: 4px;
}

.survey-page--new .question-card {
  position: relative;
  padding: 22px 18px 26px;
}

.survey-page--new .question-text {
  font-size: 19px;
  font-weight: 600;
  color: rgba(10, 20, 45, 0.9);
  padding-right: 44px;
  margin: 0 0 16px;
  line-height: 1.35;
}

.survey-page--new .question-ai-help {
  margin: -4px 0 18px;
}

.survey-page--new .question-ai-help__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(32, 90, 255, 0.35);
  background: rgba(32, 90, 255, 0.06);
  color: rgba(32, 70, 180, 0.95);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.survey-page--new .question-ai-help__toggle:hover:not(:disabled) {
  background: rgba(32, 90, 255, 0.11);
  border-color: rgba(32, 90, 255, 0.5);
}

.survey-page--new .question-ai-help__toggle:disabled {
  opacity: 0.55;
  cursor: wait;
}

.survey-page--new .question-ai-help__panel {
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(10, 20, 45, 0.04);
  border: 1px solid rgba(10, 20, 45, 0.08);
}

.survey-page--new .question-ai-help__status {
  margin: 0;
  font-size: 14px;
  color: rgba(10, 20, 45, 0.55);
}

.survey-page--new .question-ai-help__err {
  margin: 0;
  font-size: 14px;
  color: rgba(185, 28, 28, 0.9);
}

.survey-page--new .question-ai-help__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(10, 20, 45, 0.85);
  white-space: pre-wrap;
}

.survey-page--new .answer-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.survey-page--new .answer-options--binary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  max-width: 520px;
  margin: 0 auto;
}

.survey-page--new .answer-options--binary .level-option {
  justify-content: center;
  text-align: center;
  min-height: 76px;
  font-size: 17px;
  font-weight: 700;
}

.survey-page--new .level-option--binary-optimal.selected {
  border-color: rgba(16, 185, 129, 0.55);
  background: rgba(16, 185, 129, 0.12);
}

.survey-page--new .level-option--binary-basic.selected {
  border-color: rgba(239, 68, 68, 0.45);
  background: rgba(239, 68, 68, 0.08);
}

.survey-page--new .level-option {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 14px;
  border: 1px solid rgba(10, 20, 45, 0.1);
  font-weight: 500;
  text-align: left;
  line-height: 1.45;
  padding: 12px 14px;
  background: #fafbfc;
  color: rgba(16, 28, 58, 0.92);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.survey-page--new .level-option__badge {
  flex: 0 0 auto;
  min-width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.95rem;
  background: rgba(10, 20, 45, 0.07);
  color: rgba(10, 20, 45, 0.88);
}

.survey-page--new .level-option__text {
  flex: 1;
  min-width: 0;
  font-size: 15px;
}

.survey-page--new .level-option:hover:not(.selected) {
  transform: translateY(-1px);
  border-color: rgba(32, 90, 255, 0.35);
  box-shadow: 0 6px 16px rgba(32, 90, 255, 0.08);
  background: rgba(32, 90, 255, 0.04);
}

.survey-page--new .level-option--n1 .level-option__badge {
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
}
.survey-page--new .level-option--n2 .level-option__badge {
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
}
.survey-page--new .level-option--n3 .level-option__badge {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}
.survey-page--new .level-option--n4 .level-option__badge {
  background: rgba(14, 165, 233, 0.12);
  color: #0369a1;
}
.survey-page--new .level-option--n5 .level-option__badge {
  background: rgba(34, 197, 94, 0.14);
  color: #15803d;
}

.survey-page--new .level-option.selected {
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.95), rgba(37, 99, 235, 0.88)) !important;
  color: #fff !important;
  border-color: rgba(32, 90, 255, 0.5) !important;
  font-weight: 600;
  box-shadow: 0 8px 22px rgba(32, 90, 255, 0.22);
}

.survey-page--new .level-option.selected .level-option__badge {
  background: rgba(255, 255, 255, 0.22) !important;
  color: #fff !important;
}

.survey-page--new .team-btn {
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.88), rgba(0, 194, 255, 0.72));
  border: 1px solid rgba(32, 90, 255, 0.25);
  box-shadow: 0 8px 22px rgba(32, 90, 255, 0.18);
  color: #fff;
  font-weight: 600;
  padding: 12px 20px;
}

.survey-page--new .team-btn:hover {
  filter: brightness(1.06);
  transform: translateY(-2px);
}

.survey-page--new .team-btn.active {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.92), rgba(16, 185, 129, 0.78));
  border-color: rgba(34, 197, 94, 0.35);
}

.survey-page--new .modern-button.purple {
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.92), rgba(0, 194, 255, 0.82));
  box-shadow: 0 12px 32px rgba(32, 90, 255, 0.22);
  min-width: 240px;
}

.survey-page--new .modern-button.purple:hover {
  transform: translateY(-2px);
}

.survey-page--new .back-btn {
  border-radius: 12px;
  border: 1px solid rgba(10, 20, 45, 0.14);
  background: rgba(255, 255, 255, 0.92);
  padding: 7px 10px;
  cursor: pointer;
  position: absolute;
  top: 18px;
  right: 18px;
  box-shadow: 0 6px 16px rgba(10, 20, 45, 0.08);
}

.survey-submit-wrap {
  text-align: center;
  margin-top: 22px;
}

.survey-page--new .team-selection {
  margin: 0;
}

.survey-page--new .create-btn {
  margin-top: 16px;
  cursor: pointer;
  font-weight: 700;
  font-size: 15px;
  color: rgba(32, 90, 255, 0.95);
}

.survey-page--new .create-btn:hover {
  text-decoration: underline;
}

</style>
